import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { FAMILY_HISTORY } from '@constants';
import { FamilyHistoryData } from '@models/medical-history.model';
import { TranslateService } from '@ngx-translate/core';
import { AlertService } from '@services/alert.service';
import { MedicalHistoryService } from '@services/medical-history.service';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

@Component({
  selector: 'gc-family-history',
  templateUrl: './family-history.component.html',
  styleUrls: ['./family-history.component.scss'],
  standalone: false,
})
export class FamilyHistoryComponent implements OnInit, OnChanges, OnDestroy {
  @Input() userId: number;
  @Input() familyHistory: FamilyHistoryData[];
  @Input() requestUuid: string | null = null;
  @Output() submittedStep = new EventEmitter<{ step: string; values: any }>();

  @ViewChild('input_condition') inputFilter: ElementRef<HTMLInputElement>;

  subscriptions: Subscription[] = [];
  familyHistoryForm: FormGroup;
  familyHistoryOptions = FAMILY_HISTORY;
  familyConditionListForm = this.formBuilder.group({
    familyConditionList: this.formBuilder.array([], null),
  });

  constructor(
    private readonly alertService: AlertService,
    private readonly medicalHistoryService: MedicalHistoryService,
    private readonly formBuilder: FormBuilder,
    private readonly translateService: TranslateService,
  ) {}

  get selectedOption() {
    return this.familyHistoryForm.get('description').value ?? undefined;
  }

  ngOnInit() {
    this.buildFamilyHistoryForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['familyHistory'] && !changes['familyHistory'].firstChange) {
      this.buildFamilyHistoryForm();
    }
  }

  buildFamilyHistoryForm() {
    this.familyHistory = this.familyHistory ?? [];
    this.familyHistoryForm = this.formBuilder.group({
      description: ['', [Validators.required]],
      value: ['true'],
      note: [''],
    });
    this.familyConditionList.clear(); // Clear existing form array controls
    this.familyHistory.forEach((familyHistoryItem) => {
      if (!familyHistoryItem.value) {
        return;
      }
      const familyHistory = this.formBuilder.group({
        description: [familyHistoryItem.description, [Validators.required]],
        value: ['true'],
        note: [familyHistoryItem.note],
      });
      this.familyConditionList.push(familyHistory);
    });
  }

  filterCondition() {
    const value = this.inputFilter.nativeElement.value;
    if (!value) {
      this.familyHistoryOptions = FAMILY_HISTORY;
      return;
    }
    this.familyHistoryOptions = FAMILY_HISTORY.filter((option) => option.toLowerCase().includes(value.toLowerCase()));
  }

  addFamilyHistoryItem() {
    if (this.familyHistoryForm.valid) {
      const familyHistoryItem: FamilyHistoryData = this.familyHistoryForm.value;

      if (this.familyConditionList.value.findIndex((item) => item.description === familyHistoryItem.description) >= 0) {
        this.alertService.snackbar('this_family_history_item_already_exists');
        return;
      }
      this.familyHistoryForm.reset();

      const doctorChat = this.formBuilder.group({
        description: [familyHistoryItem.description, [Validators.required]],
        value: ['true'],
        note: [familyHistoryItem.note],
      });
      this.familyConditionList.insert(0, doctorChat);
    }
  }

  onOptionSelected(event: MatAutocompleteSelectedEvent): void {
    this.familyHistoryForm.get('description').setValue(this.translateService.instant(event.option.value));
  }

  deleteFamilyHistoryItem(index: number) {
    this.familyConditionList.removeAt(index);
  }

  get familyConditionList(): FormArray {
    return this.familyConditionListForm.get('familyConditionList') as FormArray;
  }

  saveToProfile() {
    this.subscriptions.push(
      this.medicalHistoryService
        .saveMedicalHistory$(this.userId, 'family_history', { user_id: this.userId, family_history: this.familyConditionList.value })
        .pipe(take(1))
        .subscribe(() => this.alertService.snackbar('family_history_saved')),
    );
  }

  saveToRequest() {
    this.medicalHistoryService
      .saveRequestMedicalHistory$(this.requestUuid, 'family_history', this.familyConditionList.value)
      .pipe(take(1))
      .subscribe((request) => {
        if (request) {
          this.submittedStep.emit({
            step: 'familyHistory',
            values: this.familyConditionList.value,
          });
          this.alertService.snackbar('family_history_saved');
        }
      });
  }

  submit() {
    if (this.requestUuid) {
      this.saveToRequest();
    } else {
      this.saveToProfile();
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
